import React from 'react';
import {
  C,
  CARD_W, PUZZLE_CARD_H, PUZZLE_CARD_GAP,
  CIRCLE_R, PLUS_R, BRANCH_STEM,
  imgUrl, normalizeImgField,
} from '../flowCanvas.constants';

// Card internal measurements
const HEADER_H  = 32;    // title / puzzle name bar
const PAD_H     = 8;     // side paddings inside card
const IMG_W     = CARD_W - PAD_H * 2;   // image width
const IMG_H     = 100;   // puzzle image area height
const DIV_Y_OFF = HEADER_H + PAD_H + IMG_H + PAD_H;  // top of divider below image
const SOL_LABEL_H = 16;  // "Solution Image" sub-label height
const SOL_IMG_H   = 100; // solution image area height (match puzzle image area)
const CARD_CORNER = 8;

// ─── PuzzleCard — renders one puzzle card at (cx, cy) ──────────────────────
function PuzzleCard({
  item,
  index,
  cx,
  cy,
  selected,
  onClick,
  onAddSolutionClick,
  onRemoveSolutionClick,
  onRemovePuzzleClick,
  assetFolder,
  campaignId,
}) {
  const left = cx - CARD_W / 2;
  const top  = cy - PUZZLE_CARD_H / 2;

  const name = item?.name || `Puzzle ${index + 1}`;

  /* ── Puzzle image ── */
  const puzzleImgNorm = normalizeImgField(item?.puzzleImage);
  const puzzleImgSrc  = puzzleImgNorm.srcUrl
    ? puzzleImgNorm.srcUrl
    : puzzleImgNorm.name
      ? imgUrl(puzzleImgNorm.name, puzzleImgNorm.isDefault, assetFolder, campaignId)
      : null;
  const hasPuzzleImg = !!puzzleImgSrc;
  const accent = hasPuzzleImg ? C.purple : C.purpleDim;

  /* ── Solution flags ── */
  const sameAsPuzzle   = item?.solutionImageIsPuzzleImage !== false; // default true
  const solNorm        = normalizeImgField(item?.solutionImage);
  const solSrc         = solNorm.srcUrl
    ? solNorm.srcUrl
    : solNorm.name
      ? imgUrl(solNorm.name, solNorm.isDefault, assetFolder, campaignId)
      : null;
  const hasSolImg      = !!solSrc && !sameAsPuzzle;

  /* ── Layout Y positions inside the card ── */
  const imgY       = top + HEADER_H + PAD_H;  // top of puzzle image rect
  const dividerY   = top + DIV_Y_OFF;
  const solLabelY  = dividerY + 6;
  const solAreaY   = solLabelY + SOL_LABEL_H + 4;

  /* ── Image clip IDs (unique per card position) ── */
  const puzzleClipId  = `pzl-clip-puzzle-${(cx | 0)}-${(cy | 0)}`;
  const solClipId     = `pzl-clip-sol-${(cx | 0)}-${(cy | 0)}`;
  const puzzleCheckerId = `pzl-checker-puzzle-${(cx | 0)}-${(cy | 0)}`;
  const solCheckerId = `pzl-checker-sol-${(cx | 0)}-${(cy | 0)}`;

  return (
    <g className="fc-node fc-puzzle-card" onClick={onClick} style={{ cursor: 'pointer' }}>
      <defs>
        <clipPath id={puzzleClipId}>
          <rect x={left + PAD_H} y={imgY} width={IMG_W} height={IMG_H} rx="4" />
        </clipPath>
        <clipPath id={solClipId}>
          <rect x={left + PAD_H} y={solAreaY} width={IMG_W} height={SOL_IMG_H} rx="4" />
        </clipPath>
        <pattern id={puzzleCheckerId} width="12" height="12" patternUnits="userSpaceOnUse">
          <rect width="12" height="12" fill="#0f172a" />
          <rect width="6" height="6" fill="#1e293b" />
          <rect x="6" y="6" width="6" height="6" fill="#1e293b" />
        </pattern>
        <pattern id={solCheckerId} width="12" height="12" patternUnits="userSpaceOnUse">
          <rect width="12" height="12" fill="#0f172a" />
          <rect width="6" height="6" fill="#1e293b" />
          <rect x="6" y="6" width="6" height="6" fill="#1e293b" />
        </pattern>
      </defs>

      {/* Drop shadow */}
      <rect x={left + 2} y={top + 4} width={CARD_W} height={PUZZLE_CARD_H}
        rx={CARD_CORNER} fill="#000" fillOpacity="0.3" />

      {/* Card body */}
      <rect x={left} y={top} width={CARD_W} height={PUZZLE_CARD_H}
        rx={CARD_CORNER}
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : accent}
        strokeWidth={selected ? 2 : 1.5}
      />

      {/* Header — puzzle name + index badge */}
      <rect x={left} y={top} width={CARD_W} height={HEADER_H}
        rx={CARD_CORNER} fill="rgba(145,70,255,0.12)" />
      <rect x={left} y={top + HEADER_H - 6} width={CARD_W} height={6} fill="rgba(145,70,255,0.06)" />

      <text x={left + 10} y={top + HEADER_H / 2 + 1}
        dominantBaseline="middle" fontSize="10.5" fontWeight="700" fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {name.length > 22 ? name.slice(0, 22) + '…' : name}
      </text>

      {/* Index badge — shifted left to make room for delete button */}
      <circle cx={left + CARD_W - 30} cy={top + HEADER_H / 2} r={9} fill={accent} fillOpacity="0.25" />
      <text x={left + CARD_W - 30} y={top + HEADER_H / 2 + 1}
        textAnchor="middle" dominantBaseline="middle"
        fontSize="8.5" fontWeight="700" fill={accent}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {index + 1}
      </text>

      {/* Delete button */}
      {onRemovePuzzleClick && (
        <g
          onClick={(e) => { e.stopPropagation(); onRemovePuzzleClick(index); }}
          style={{ cursor: 'pointer' }}
        >
          <circle cx={left + CARD_W - 11} cy={top + HEADER_H / 2} r={9} fill="#dc2626" />
          <g transform={`translate(${left + CARD_W - 11}, ${top + HEADER_H / 2})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
            <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
            <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
            <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
            <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
          </g>
        </g>
      )}

      {/* Puzzle Image area */}
      <rect x={left + PAD_H} y={imgY} width={IMG_W} height={IMG_H} rx="4"
        fill={`url(#${puzzleCheckerId})`} clipPath={`url(#${puzzleClipId})`} />

      {hasPuzzleImg ? (
        <image
          href={puzzleImgSrc}
          x={left + PAD_H} y={imgY}
          width={IMG_W} height={IMG_H}
          preserveAspectRatio="xMidYMid meet"
          clipPath={`url(#${puzzleClipId})`}
          style={{ pointerEvents: 'none' }}
        />
      ) : (
        <>
          <rect x={left + PAD_H} y={imgY} width={IMG_W} height={IMG_H} rx="4"
            fill="none" stroke={C.border} strokeWidth="1" strokeDasharray="4 3" />
          <text x={cx} y={imgY + IMG_H / 2 - 8}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="18" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>🧩</text>
          <text x={cx} y={imgY + IMG_H / 2 + 10}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="8.5" fill={C.textMuted}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>No puzzle image</text>
        </>
      )}

      <rect x={left + PAD_H} y={imgY} width={IMG_W} height={IMG_H} rx="4"
        fill="none" stroke={accent} strokeWidth="0.75" strokeOpacity="0.35" />

      {/* Divider + Solution section */}
      <line x1={left + 8} y1={dividerY} x2={left + CARD_W - 8} y2={dividerY}
        stroke={C.border} strokeWidth="0.8" />

      <text x={left + 10} y={solLabelY + SOL_LABEL_H / 2 + 1}
        dominantBaseline="middle" fontSize="8.5" fontWeight="600" fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>Solution Image</text>

      {/* "Same as puzzle image" chip */}
      {sameAsPuzzle && (
        <g
          onClick={(e) => { e.stopPropagation(); onAddSolutionClick && onAddSolutionClick(index); }}
          style={{ cursor: 'pointer' }}
        >
          <rect x={left + PAD_H} y={solAreaY} width={IMG_W} height={SOL_IMG_H}
            rx="6" ry="6"
            fill="rgba(0,0,0,0.001)"
            stroke={`${C.green}88`}
            strokeWidth="1"
            strokeDasharray="4 3"
            pointerEvents="all" />
          <text x={cx} y={solAreaY + SOL_IMG_H / 2 - 14}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="16" fill={C.green}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>+</text>
          <text x={cx} y={solAreaY + SOL_IMG_H / 2 + 1}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="9.5" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>Same as puzzle image</text>
          <text x={cx} y={solAreaY + SOL_IMG_H / 2 + 18}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="9.5" fill={C.green}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>Click to add custom image</text>
        </g>
      )}

      {/* Custom solution image */}
      {!sameAsPuzzle && (
        hasSolImg ? (
          <>
            <rect x={left + PAD_H} y={solAreaY} width={IMG_W} height={SOL_IMG_H} rx="4"
              fill={`url(#${solCheckerId})`} clipPath={`url(#${solClipId})`} />
            <image
              href={solSrc}
              x={left + PAD_H} y={solAreaY}
              width={IMG_W} height={SOL_IMG_H}
              preserveAspectRatio="xMidYMid meet"
              clipPath={`url(#${solClipId})`}
              style={{ pointerEvents: 'none' }}
            />
            <rect x={left + PAD_H} y={solAreaY} width={IMG_W} height={SOL_IMG_H} rx="4"
              fill="none" stroke={accent} strokeWidth="0.75" strokeOpacity="0.35" />

            {onRemoveSolutionClick && (
              <g
                onClick={(e) => { e.stopPropagation(); onRemoveSolutionClick(index); }}
                style={{ cursor: 'pointer' }}
              >
                <circle cx={left + PAD_H + IMG_W} cy={solAreaY} r={11} fill="#dc2626" />
                <g transform={`translate(${left + PAD_H + IMG_W}, ${solAreaY})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
                  <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
                  <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
                  <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                  <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                </g>
              </g>
            )}
          </>
        ) : (
          <>
            <rect x={left + PAD_H} y={solAreaY} width={IMG_W} height={SOL_IMG_H} rx="4"
              fill="#1a2540" stroke={C.orange} strokeWidth="1" strokeDasharray="4 3" />
            <text x={cx} y={solAreaY + SOL_IMG_H / 2}
              textAnchor="middle" dominantBaseline="middle"
              fontSize="8.5" fill={C.orange}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>No solution image set</text>
          </>
        )
      )}
    </g>
  );
}

// ─── PuzzleStackNode — stacked puzzle card column + connector wiring ──────────
//
// Props mirror the ZapperObjectsNode pattern:
//   step              – the puzzleStack customisation step
//   stepX             – BFS X centre for the card stack
//   stepCY            – vertical centre for this node group
//   parentStepX       – X of parent circle/node (for entry connector)
//   items             – resolved puzzle items array
//   activeStepId / activeGraphicIndex – selection state
//   assetFolder / campaignId – for image URL resolution
//   onPuzzleClick(idx) – card clicked
//   onAddSolutionClick(idx) – "Add custom solution image" button clicked
//   onRemoveSolutionClick(idx) – remove custom solution image / revert toggle
//   onAddPuzzleClick() – optional + button to add new puzzle

export default function PuzzleStackNode({
  step,
  stepX,
  stepCY,
  parentStepX,
  parentStepY,
  parentHalfW,
  items = [],
  activeStepId,
  activeGraphicIndex,
  assetFolder,
  campaignId,
  onPuzzleClick,
  onAddSolutionClick,
  onRemoveSolutionClick,
  onAddPuzzleClick,
  onRemovePuzzleClick,
}) {
  const count    = Math.max(items.length, 1);
  const totalH   = count * (PUZZLE_CARD_H + PUZZLE_CARD_GAP) - PUZZLE_CARD_GAP;

  const hasParent    = parentStepX !== null && parentStepX !== undefined;
  // Use actual parent halfW (card vs circle) instead of always assuming CIRCLE_R
  const parentRightX = hasParent
    ? parentStepX + (parentHalfW ?? CIRCLE_R)
    : stepX - CARD_W / 2 - 50 + CIRCLE_R;
  // Parent may be on a different Y (e.g. col=4 centre while stack is col=3)
  const effectivePCY = (parentStepY !== null && parentStepY !== undefined) ? parentStepY : stepCY;
  const stackTopY    = stepCY - totalH / 2;
  const stackMidY    = stepCY;

  const cardCentreX  = stepX;
  const cardLeftX    = cardCentreX - CARD_W / 2;
  const branchMidX   = Math.max(parentRightX + BRANCH_STEM, (parentRightX + cardLeftX) / 2);

  const firstCardY   = stackTopY + PUZZLE_CARD_H / 2;
  const lastCardY    = stackTopY + (Math.max(items.length, 1) - 1) * (PUZZLE_CARD_H + PUZZLE_CARD_GAP) + PUZZLE_CARD_H / 2;

  // + add button sits on the horizontal stem at the parent's Y level
  const plusX        = (parentRightX + branchMidX) / 2;
  const plusY        = effectivePCY;

  const stackColor   = C.purple;

  return (
    <React.Fragment key={step.stepId}>

      {/* ── Entry connector: parent right edge → branchMidX (at parentCY) → vertical jog → card spurs ── */}
      {hasParent && (
        <g style={{ pointerEvents: 'none' }}>
          {/* Horizontal stem at parent's Y level */}
          <line x1={parentRightX} y1={effectivePCY} x2={branchMidX} y2={effectivePCY}
            stroke={stackColor} strokeWidth="1.5" strokeOpacity="0.7" strokeLinecap="square" />
          {/* Vertical jog from parentCY down/up to the stack midpoint */}
          {Math.abs(effectivePCY - stackMidY) > 2 && (
            <line x1={branchMidX} y1={effectivePCY} x2={branchMidX} y2={stackMidY}
              stroke={stackColor} strokeWidth="1.5" strokeOpacity="0.7" strokeLinecap="square" />
          )}
          {/* Vertical spine through all cards, centered on the incoming wire */}
          {(items.length > 1 || Math.abs(stackMidY - firstCardY) > 2 || Math.abs(stackMidY - lastCardY) > 2) && (
            <line x1={branchMidX} y1={Math.min(firstCardY, stackMidY)} x2={branchMidX} y2={Math.max(lastCardY, stackMidY)}
              stroke={stackColor} strokeWidth="1.5" strokeOpacity="0.5" />
          )}
          {/* Horizontal spur + dot per card */}
          {(items.length > 0 ? items : [null]).map((_, i) => {
            const cardY = stackTopY + i * (PUZZLE_CARD_H + PUZZLE_CARD_GAP) + PUZZLE_CARD_H / 2;
            return (
              <React.Fragment key={i}>
                <line x1={branchMidX} y1={cardY} x2={cardLeftX} y2={cardY}
                  stroke={stackColor} strokeWidth="1.5" strokeOpacity="0.7" strokeLinecap="square" />
                <circle cx={cardLeftX} cy={cardY} r="3" fill={stackColor} fillOpacity="0.9" />
              </React.Fragment>
            );
          })}

        </g>
      )}

      {/* ── + add-puzzle circle on stem ── */}
      {hasParent && onAddPuzzleClick && (
        <g onClick={onAddPuzzleClick} style={{ cursor: 'pointer' }}>
          <circle cx={plusX} cy={plusY} r={PLUS_R} fill={C.green} />
          <text x={plusX} y={plusY + 1}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="16" fontWeight="300" fill="#fff"
            style={{ pointerEvents: 'none', userSelect: 'none' }}>+</text>
        </g>
      )}

      {/* ── Individual puzzle cards ── */}
      {items.length > 0
        ? items.map((item, i) => {
            const cardY  = stackTopY + i * (PUZZLE_CARD_H + PUZZLE_CARD_GAP) + PUZZLE_CARD_H / 2;
            const isSel  = activeStepId === step.stepId && activeGraphicIndex === i;
            return (
              <PuzzleCard
                key={i}
                item={item}
                index={i}
                cx={cardCentreX}
                cy={cardY}
                selected={isSel}
                onClick={() => onPuzzleClick && onPuzzleClick(i)}
                onAddSolutionClick={onAddSolutionClick}
                onRemoveSolutionClick={onRemoveSolutionClick}
                onRemovePuzzleClick={onRemovePuzzleClick}
                assetFolder={assetFolder}
                campaignId={campaignId}
              />
            );
          })
        : /* Empty state */
          (
            <g key="empty" onClick={onAddPuzzleClick} style={{ cursor: 'pointer' }}>
              <rect x={cardCentreX - CARD_W / 2 + 2} y={stepCY - PUZZLE_CARD_H / 2 + 4}
                width={CARD_W - 4} height={PUZZLE_CARD_H - 8}
                rx={CARD_CORNER} fill="#1a2540" stroke={C.border}
                strokeWidth="1.2" strokeDasharray="5 3" />
              <text x={cardCentreX} y={stepCY - 10}
                textAnchor="middle" dominantBaseline="middle"
                fontSize="20" fill={C.textDim}
                style={{ pointerEvents: 'none', userSelect: 'none' }}>🧩</text>
              <text x={cardCentreX} y={stepCY + 10}
                textAnchor="middle" dominantBaseline="middle"
                fontSize="9" fill={C.textMuted}
                style={{ pointerEvents: 'none', userSelect: 'none' }}>No puzzles configured</text>
            </g>
          )
      }
    </React.Fragment>
  );
}
